const FLIP_DURATION_MS = 250;
const MIN_MOVEMENT_PX = 1;
const MATRIX_2D_TRANSLATE_Y_INDEX = 5;
const MATRIX_3D_TRANSLATE_Y_INDEX = 13;

function rowTopInContainer(row: HTMLElement, container: HTMLElement): number {
  return (
    row.getBoundingClientRect().top - container.getBoundingClientRect().top
  );
}

function translateYFromMatrix(transform: string): number {
  if (transform === 'none') return 0;

  const match = transform.match(/^matrix(3d)?\((.+)\)$/);
  if (!match) return 0;

  const [, dimension, rawValues] = match;
  const values = rawValues
    .split(',')
    .map((value) => Number.parseFloat(value.trim()));
  return dimension === '3d'
    ? (values[MATRIX_3D_TRANSLATE_Y_INDEX] ?? 0)
    : (values[MATRIX_2D_TRANSLATE_Y_INDEX] ?? 0);
}

function rowTranslateY(row: HTMLElement): number {
  const inlineMatch = row.style.transform.match(/translateY\(([-\d.]+)px\)/);
  if (inlineMatch) {
    return Number.parseFloat(inlineMatch[1]);
  }

  return translateYFromMatrix(getComputedStyle(row).transform);
}

/**
 * Reconstruct where FLIP rows were last painted. After a follow-up re-sort,
 * Solid has already written new `top` values, so `getBoundingClientRect`
 * would read `newLayout + oldTransform`. Use the layout captured at invert
 * time plus the current transform instead.
 */
export function interruptedFlipRowPositions(
  rows: readonly HTMLElement[],
  layoutTopsAtInvert: ReadonlyMap<string, number>
): Map<string, number> {
  const positions = new Map<string, number>();
  rows.forEach((row) => {
    const id = row.dataset['stopId'];
    if (!id) return;
    const layoutTop = layoutTopsAtInvert.get(id);
    if (layoutTop === undefined) return;
    positions.set(id, layoutTop + rowTranslateY(row));
  });
  return positions;
}

/**
 * Build a full stop-row position snapshot after an interrupted FLIP. Unmoved
 * rows (below `MIN_MOVEMENT_PX`) keep their current layout tops; moved rows
 * use invert-time layout plus the active transform so follow-up re-sorts do
 * not drop them from `firstPositions`.
 */
function mergeInterruptedFlipPositions(
  container: HTMLElement,
  movedRows: readonly HTMLElement[],
  layoutTopsAtInvert: ReadonlyMap<string, number>
): Map<string, number> {
  const positions = snapshotPaletteStopRowPositions(container);
  interruptedFlipRowPositions(movedRows, layoutTopsAtInvert).forEach(
    (top, id) => positions.set(id, top)
  );
  return positions;
}

/**
 * Snapshot the vertical position of every stop-row inside `container`,
 * keyed by `data-stop-id`. Positions are relative to the container so
 * scrolling the editor panel does not corrupt deltas between passes.
 */
export function snapshotPaletteStopRowPositions(
  container: HTMLElement
): Map<string, number> {
  const positions = new Map<string, number>();
  const rows = container.querySelectorAll<HTMLElement>('[data-stop-id]');
  rows.forEach((row) => {
    const id = row.dataset['stopId'];
    if (id) positions.set(id, rowTopInContainer(row, container));
  });
  return positions;
}

/**
 * FLIP animate stop-rows from their `firstPositions` to their current
 * (post-sort) positions inside `container`.
 */
export function flipAnimatePaletteStopRows(
  container: HTMLElement,
  firstPositions: ReadonlyMap<string, number>,
  onSettled?: (positions?: ReadonlyMap<string, number>) => void
): () => void {
  let invertFrame = 0;
  let playFrame = 0;
  let settled = false;
  const movedRows: HTMLElement[] = [];
  const layoutTopsAtInvert = new Map<string, number>();
  const removeListeners: (() => void)[] = [];

  const settle = (interruptedPositions?: ReadonlyMap<string, number>): void => {
    if (settled) return;
    settled = true;
    onSettled?.(interruptedPositions);
  };

  invertFrame = requestAnimationFrame(() => {
    const rows = container.querySelectorAll<HTMLElement>('[data-stop-id]');
    rows.forEach((row) => {
      const id = row.dataset['stopId'];
      if (!id) return;
      const firstTop = firstPositions.get(id);
      if (firstTop === undefined) return;

      const layoutTop = rowTopInContainer(row, container);
      const delta = firstTop - layoutTop;
      if (Math.abs(delta) < MIN_MOVEMENT_PX) return;

      layoutTopsAtInvert.set(id, layoutTop);
      row.style.transform = `translateY(${String(delta)}px)`;
      row.style.transition = 'none';
      movedRows.push(row);
    });

    if (movedRows.length === 0) {
      settle();
      return;
    }

    playFrame = requestAnimationFrame(() => {
      let pendingTransitions = movedRows.length;

      movedRows.forEach((row) => {
        row.style.transition = `transform ${String(FLIP_DURATION_MS)}ms ease`;
        row.style.transform = '';

        const onEnd = (): void => {
          row.style.transition = '';
          row.removeEventListener('transitionend', onEnd);
          pendingTransitions -= 1;
          if (pendingTransitions === 0) {
            settle();
          }
        };
        row.addEventListener('transitionend', onEnd);
        removeListeners.push(() =>
          row.removeEventListener('transitionend', onEnd)
        );
      });
    });
  });

  return () => {
    cancelAnimationFrame(invertFrame);
    cancelAnimationFrame(playFrame);
    removeListeners.forEach((remove) => remove());

    const interruptedPositions =
      !settled && movedRows.length > 0
        ? mergeInterruptedFlipPositions(
            container,
            movedRows,
            layoutTopsAtInvert
          )
        : undefined;

    movedRows.forEach((row) => {
      row.style.transition = '';
      row.style.transform = '';
    });

    if (settled) return;
    settled = true;
    if (interruptedPositions) {
      onSettled?.(interruptedPositions);
    }
  };
}
